import type { IntakeQuestionsProfileControls } from './IntakeBand';
import ClinicalQuestionDetailsInput from './ClinicalQuestionDetails';
import { clinicalQuestionKind, clinicalDetailSummary, clinicalDetailsSatisfyRequiredDetail, clinicalDetailsPending, hydrateClinicalDetails, serializeClinicalDetails, type ClinicalQuestionDetailDraft, type ClinicalQuestionTerm } from './clinical-question-details-view';
import { Checkbox } from '@/components/ui/checkbox';
import ChoiceSelect from '@/dashboard/ui/ChoiceSelect';
/**
 * AdoraCare -- THE CONSULT INTAKE BLOCK
 * (REGISTRATION-CONTRACT-AND-WALK-CLOSURE-V1, D5 -- THE CLINICAL RELOCATION;
 * reworked by DOCTOR-INTAKE-ONE-FLOW-V1, founder ruling 2026-08-14).
 *
 * WHAT MOVED AND WHY (D5, unchanged): "A few questions for the doctor" used to
 * render on the FRONT DESK's registration form. Registration is an
 * ADMINISTRATIVE act and those questions are CLINICAL context, so they open
 * the DOCTOR's consult as its first working block, reading and writing THE
 * SAME underlying data through THE SAME routes
 * (GET/POST /api/appointments/:id/intake/questionnaire). The question
 * DEFINITIONS remain the org's own `preconsult.question_set` config.
 *
 * ONE FLOW (DOCTOR-INTAKE-ONE-FLOW-V1 -- the 2026-08-14 Tower glass-check
 * ruling; LES-150):
 *   R1  The "Mark intake reviewed" act, the #506 completeness gate, and the
 *       awaiting-review chip are GONE. Patient-reported data is context the
 *       doctor confirms by DOING the intake (the W-30 fold doctrine,
 *       completed); review responsibility lives in the close act's advisory
 *       gap lines (WARN-only, the standing close ruling).
 *   R2  The "Save intake answers" button is GONE. Answers AUTOSAVE on tap
 *       through the SAME questionnaire route -- the chief-complaint note's
 *       exact idiom (useConsultAutosave: debounce, single-flight, trailing
 *       coalesce, flush-on-unmount) -- with the ONE save-state line rendered
 *       as a small "Saved" tick in the panel head (and, from the same ONE
 *       state, a tick beside the last-tapped question). New trigger only;
 *       the writer, routes, and role doors are byte-unchanged.
 *   R3a `reason_for_visit` is NO LONGER FILTERED OUT: "What brings the
 *       patient in today?" (the doctor-voice render of the config question --
 *       a render-law label; the config prompt itself is untouched and the
 *       patient tablet keeps its own voice) is the FIRST visible question,
 *       free text, and IS the chief complaint -- one owner. Typing here
 *       write-throughs to the case-sheet form's chiefComplaint via
 *       `onReasonChange` (the sheet's own autosave persists that store); the
 *       questionnaire autosave persists the reason answer. One input, both
 *       existing writers. GATE SET == VISIBLE SET (LES-150): every required
 *       key the completeness derivation can name now renders here, so no
 *       gate or advisory can ever demand a field the doctor cannot see.
 *   R3b The panel is LABELED "Patient intake" (the honest-panels law; the
 *       sheet rail is "Doctor's record").
 *
 * OPTION A (founder approval in this chat, 2026-09-11): compact entry
 * keeps autosave; Save intake flushes the existing writer and shows a
 * confirmed summary. Edit intake reopens it. No new questions or routes.
 * The historical COLLAPSE-ON-COMPLETE fold (CONSULT-INTAKE-RENDER-HEAL-V1 scope 2) is
 * RETIRED with its trigger: it keyed on the reviewed stamp, and the
 * founder-approved one-flow mockup (and the product pass gate's re-walk:
 * "return -> answers render selected") shows the answered panel OPEN.
 *
 * NO SILENT WRITES, amended honestly: viewing this block still writes
 * NOTHING; the autosave writes ONLY what the doctor authored (a tap or typed
 * text), through the explicit capture door
 * (api/routes/patient-preconsult.ts PRECONSULT_CAPTURE_ROLES) -- the same
 * authorization as the retired button, per-edit instead of per-click.
 *
 * HONEST DEGRADE: a non-clinical audience (the wire law) or an identity
 * review yields no editable block at all (LES-016: no dead controls).
 *
 * FETCH OWNERSHIP (CONSULT-START-TRUTH-V1, W-32 -- IDLE-MEANS-IDLE,
 * unchanged at entry): the page's ONE questionnaire read arrives as the
 * `read` prop. Acknowledged saves and Discard/recovery recheck that same API;
 * it does not add a mount/idle fetch or a second persistence store.
 */

import { historyIntakeAbsentCopy } from '@/dashboard/doctor/doctor-patient-lens-view';
import { useEffect, useRef, useState } from 'react';
import { PortalApiError } from '@/lib/portal-api';

import {
  saveIntakeQuestionnaire,
  getIntakeQuestionnaire,
  type ConsultContinuityWire,
  type PreconsultQuestionSpec,
  type PreconsultReadResult,
  type PreconsultResumeAnswer,
  type PreconsultAnswerInput,
} from '@/dashboard/api';
import { type PanelState } from '@/dashboard/doctor/PreconsultIntakePanel';
import { AcButton, SectionShell } from '@/dashboard/ui';
import {
  useConsultAutosave,
  consultSaveStateTone,
  shouldWarnOnNavigate,
  type ConsultSaveState,
} from '@/dashboard/doctor/consult-autosave';
import { formatIstShortDate, formatIstTime } from '@/lib/datetime-ist';
import {
  buildQuestionnairePayload,
  hydrateAnswerState,
  isAnswered,
  isOptionSelected,
  questionNeedsDetail,
  toggleMultiOption,
  type QuestionnaireAnswerState,
} from '@/dashboard/appointments/preconsult-questionnaire-view';
import {
  IntakeChoiceChips,
  IntakeQuestionSection,
} from '@/dashboard/appointments/PatientIntakeScreens';

/** R3b -- the honest panel label (left panel of the intake area). */
export const CONSULT_INTAKE_PANEL_TITLE = 'Patient intake';
/** The block's intro -- R2 wording: the save is the tap. */
export const CONSULT_INTAKE_INTRO =
  'Ask these at the start of the consult. Answers save as you tap.';
/** R3a -- the DOCTOR-VOICE render of the `reason_for_visit` config question
 *  (render law: a display label for this surface; the config prompt is
 *  untouched and patient surfaces keep their own voice). */
export const CONSULT_REASON_PROMPT = 'What brings the patient in today?';
/** R3a -- the one-owner statement under the reason input (product copy). */
export const CONSULT_REASON_IS_COMPLAINT_COPY = 'This is the chief complaint.';
/** The honest empty seam: the questionnaire is org config, so an org with no
 *  configured questions has NOTHING to ask -- a real state, not a failure. */
export const CONSULT_INTAKE_NO_QUESTIONS_COPY =
  'No intake questions are configured for this clinic.';
export const CONSULT_INTAKE_UNDER_REVIEW_COPY =
  'Intake is under identity review. Resolve the identity before recording answers.';
/** R2 -- the clean-state tick copy (the ONE save-state, rendered small). */
export const CONSULT_INTAKE_SAVED_TICK = 'Draft saved';

export type ConsultIntakeBlockState =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'under_review' }
  | {
      kind: 'ready';
      questions: readonly PreconsultQuestionSpec[];
      seeded: QuestionnaireAnswerState;
    };

/**
 * R3a -- the block's question list: the served org set with
 * `reason_for_visit` FIRST (it is the chief complaint, the flow's opening
 * question), then the rest in served order. Pure + exported so the
 * reason-first law is a pinned unit. (The old state derivation FILTERED
 * reason_for_visit out as desk-owned -- the exact hole that let the #506
 * gate demand a field the doctor could not see; superseded by
 * DOCTOR-INTAKE-ONE-FLOW-V1.)
 */
export function consultIntakeQuestions(
  questions: readonly PreconsultQuestionSpec[],
): readonly PreconsultQuestionSpec[] {
  const reason = questions.filter((q) => q.key === 'reason_for_visit');
  const rest = questions.filter((q) => q.key !== 'reason_for_visit');
  return [...reason, ...rest];
}

/** The display prompt for a question on THIS surface (the doctor asks the
 *  patient, so the reason question renders in the doctor's voice). */
export function consultPromptFor(q: PreconsultQuestionSpec): string {
  return q.key === 'reason_for_visit' ? CONSULT_REASON_PROMPT : q.prompt;
}

/**
 * Map the role-shaped read into the block state. PURE + exported so the
 * "a non-clinical audience gets NO editable block" guarantee stays a pinned
 * unit.
 */
export function consultIntakeStateFromRead(
  res: PreconsultReadResult,
): ConsultIntakeBlockState {
  if (res.audience !== 'clinical') return { kind: 'hidden' };
  if (res.underIdentityReview) return { kind: 'under_review' };
  const questions = consultIntakeQuestions(res.questions);
  if (questions.length === 0) return { kind: 'hidden' };
  return {
    kind: 'ready',
    questions,
    seeded: hydrateAnswerState(res.resumeAnswers ?? []),
  };
}

/**
 * Map the LIFTED panel read (the page's one questionnaire fetch) into this
 * block's state. PURE + exported (unchanged seam):
 *   loading -> loading; ready -> the role-shaped derivation;
 *   unavailable/error -> hidden (a read fault hides the block rather than
 *   showing a broken editor -- the consult is never blocked on intake).
 */
export function consultIntakeStateFromPanel(
  panel: PanelState,
): ConsultIntakeBlockState {
  if (panel.kind === 'loading') return { kind: 'loading' };
  if (panel.kind === 'ready') return consultIntakeStateFromRead(panel.view);
  return { kind: 'hidden' };
}

/**
 * R3a/Q1 -- the provenance hint under the reason input: "<provenance>,
 * <captured time> - This is the chief complaint." when a reported reason
 * answer exists on the served read; the bare one-owner statement otherwise.
 * Pure + exported (pinned).
 */
export function consultReasonHint(read: PanelState): string {
  if (read.kind === 'ready') {
    const answer = read.view.answers.find(
      (a) => a.questionKey === 'reason_for_visit',
    );
    if (answer !== undefined) {
      const time = formatIstTime(answer.capturedAt);
      const stamp = time
        ? `${read.view.provenanceLabel}, ${time}`
        : read.view.provenanceLabel;
      return `${stamp} - ${CONSULT_REASON_IS_COMPLAINT_COPY}`;
    }
  }
  return CONSULT_REASON_IS_COMPLAINT_COPY;
}

/**
 * R3c -- the first unanswered VISIBLE question key (the NEXT dock's Go
 * target), over the block's own reason-first question order. Pure + exported:
 * the page derives the dock target from the SAME unit the block renders by,
 * so the door can never aim at a field this surface does not show.
 */
export function consultFirstUnansweredKey(
  questions: readonly PreconsultQuestionSpec[],
  answeredKeys: ReadonlySet<string>,
): string | null {
  const first = consultIntakeQuestions(questions).find(
    (q) => !answeredKeys.has(q.key),
  );
  return first === undefined ? null : first.key;
}

/**
 * DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (C3 -- READ-ONLY PROPAGATION,
 * kept): the read-only rows a LOCKED (finalized) visit renders in place of
 * the editor -- one row per question (now INCLUDING the reason question --
 * R3a), valued from the SERVED answers, "Not recorded" when no persisted
 * answer exists. Pure + exported so the locked shape stays a pinned unit.
 */
export function consultIntakeLockedRows(
  state: ConsultIntakeBlockState,
  read: PanelState,
): ReadonlyArray<{
  readonly key: string;
  readonly prompt: string;
  readonly value: string | null;
}> {
  if (state.kind !== 'ready') return [];
  const served = read.kind === 'ready' ? read.view.answers : [];
  return state.questions.map((q) => {
    const answer = served.find((a) => a.questionKey === q.key);
    if (answer === undefined) {
      return { key: q.key, prompt: consultPromptFor(q), value: null };
    }
    const labels = answer.selectedLabels.filter((l) => l.trim() !== '');
    const detail =
      answer.detailText !== null && answer.detailText.trim() !== ''
        ? answer.detailText.trim()
        : null;
    const value =
      labels.length > 0
        ? detail !== null
          ? `${labels.join(', ')} - ${detail}`
          : labels.join(', ')
        : detail;
    return { key: q.key, prompt: consultPromptFor(q), value };
  });
}

/**
 * R2 -- the ONE save-state line for the panel head (the trust signal). The
 * SAME ConsultSaveState model the sheet's indicator renders; only the clean
 * copy differs ("Saved <time>" -- the ruled tick -- instead of "Draft saved
 * <time>"). Pure + exported (pinned).
 */
export function consultIntakeSaveLine(state: ConsultSaveState): string | null {
  switch (state.kind) {
    case 'idle':
      return null;
    case 'clean':
      return `${CONSULT_INTAKE_SAVED_TICK} ${state.savedAt}`;
    case 'dirty':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving...';
    case 'error':
      return `Not saved - ${state.message}`;
  }
}

/* --------------------------------------------------------------------------
 * DOCTOR-INTAKE-HISTORY-V1 -- CONTINUE-CARE (founder rulings LAKSHMI-2 +
 * LAKSHMI-3, 2026-08-16/17). RETURNING PATIENT != NEW PATIENT;
 * HISTORY-EXISTS != CONTINUITY-EXISTS. A returning patient's intake must
 * never arrive blank: the block consumes THE ONE continuity summary
 * (lib/domain/clinical/visit-continuity.ts via the doctor questionnaire
 * read -- this surface is the owner's chartered SECOND consumer; no second
 * summary implementation exists here) and the prior visit's answers as the
 * carry-forward seed. Carried answers PRE-FILL marked "from <date> - tap
 * to change" and satisfy the required gates the moment the doctor's ONE
 * explicit act confirms them; the question list stays COLLAPSED by
 * default; safety facts render one-line with the confirm-current caption;
 * ONE question replaces the re-interview ("What changed since the last
 * visit?" [No changes] [Update]); the chief complaint alone stays fresh
 * each visit. NO SILENT WRITES stands: the carry seed renders and counts
 * for nothing until the doctor's confirm/update act copies it into the
 * editable state -- that act (and only that act) triggers the SAME
 * autosave POST as any tap.
 * ------------------------------------------------------------------------ */

/** The ONE what-changed question (the returning visit's whole ceremony). */
export const CONSULT_CARRY_QUESTION = 'What changed since the last visit?';
export const CONSULT_CARRY_NO_CHANGES_LABEL = 'No changes';
export const CONSULT_CARRY_UPDATE_LABEL = 'Update';
/** Mirror of CONTINUITY_INTAKE_ABSENT_COPY
 *  (lib/domain/clinical/visit-continuity.ts -- the owner's one labeled-
 *  absence voice; equality is pinned by unit test, never a second voice). */
export const CONSULT_CC_INTAKE_ABSENT_COPY =
  'Previous intake not captured digitally for this visit.';

/** The per-field carry provenance mark (ruled copy). Pure + exported. */
export function consultCarryMark(fromDateLabel: string): string {
  return `from ${fromDateLabel} - tap to change`;
}

/** The safety block's confirm-current caption (ruled copy). */
export function consultCarryConfirmLine(lastConfirmedLabel: string): string {
  return `Confirm current - last confirmed ${lastConfirmedLabel}`;
}

/** The post-confirm summary line (the collapsed state's one truth line). */
export function consultCarryConfirmedLine(fromDateLabel: string): string {
  return `Confirmed current - carried from ${fromDateLabel}`;
}

/** The carry-forward state derived from the doctor read. Pure + exported. */
export interface ConsultCarryState {
  readonly kind: 'none' | 'carry';
  readonly continuity: ConsultContinuityWire | null;
  readonly fromDateLabel: string;
  readonly lastConfirmedLabel: string;
  /** The prefill seed: prior answers EXCLUDING reason_for_visit (the chief
   *  complaint stays fresh each visit -- ruled), keyed to hydrated state. */
  readonly seed: QuestionnaireAnswerState;
  /** The carried keys: prior-answered keys INTERSECTED with the VISIBLE
   *  served set (GATE SET == VISIBLE SET, LES-152: a retired question's
   *  prior answer never resurrects) and answer-bearing per isAnswered. */
  readonly carriedKeys: readonly string[];
  /** The prior visit's full answers (the 1-click previous-intake door). */
  readonly priorAnswers: readonly PreconsultResumeAnswer[];
}

const CARRY_NONE: ConsultCarryState = {
  kind: 'none',
  continuity: null,
  fromDateLabel: '',
  lastConfirmedLabel: '',
  seed: {},
  carriedKeys: [],
  priorAnswers: [],
};

export function consultCarryState(read: PanelState): ConsultCarryState {
  if (read.kind !== 'ready') return CARRY_NONE;
  if (read.view.audience !== 'clinical') return CARRY_NONE;
  const continuity = read.view.continuity ?? null;
  const carry = read.view.carryForward;
  if (carry === undefined) {
    return continuity === null ? CARRY_NONE : { ...CARRY_NONE, continuity };
  }
  const fromDateLabel = formatIstShortDate(carry.fromVisitDateIso);
  const lastConfirmedLabel = formatIstShortDate(
    carry.capturedAt ?? carry.fromVisitDateIso,
  );
  const visible = consultIntakeQuestions(read.view.questions);
  const nonReason = carry.answers.filter(
    (a) => a.questionKey !== 'reason_for_visit',
  );
  const seed = hydrateAnswerState(nonReason);
  const carriedKeys = visible
    .filter((q) => q.key !== 'reason_for_visit')
    .filter((q) => isAnswered(q, seed[q.key]))
    .map((q) => q.key);
  if (carriedKeys.length === 0) {
    return continuity === null ? CARRY_NONE : { ...CARRY_NONE, continuity };
  }
  return {
    kind: 'carry',
    continuity,
    fromDateLabel,
    lastConfirmedLabel,
    seed,
    carriedKeys,
    priorAnswers: carry.answers,
  };
}

/** TRUE when the carry ceremony should be OFFERED: a carry exists and the
 *  CURRENT visit has no non-reason answers of its own yet (a resumed visit
 *  renders its OWN state -- the proven one-flow behavior, untouched). */
export function consultCarryOffered(
  carry: ConsultCarryState,
  questions: readonly PreconsultQuestionSpec[],
  currentSeeded: QuestionnaireAnswerState,
): boolean {
  if (carry.kind !== 'carry') return false;
  return !questions.some(
    (q) => q.key !== 'reason_for_visit' && isAnswered(q, currentSeeded[q.key]),
  );
}

/** The previous-intake door's rows (prompt + rendered value), from the raw
 *  prior answers over the SERVED question set. Pure + exported. */
export function consultCarryRows(
  questions: readonly PreconsultQuestionSpec[],
  priorAnswers: readonly PreconsultResumeAnswer[],
): ReadonlyArray<{ readonly key: string; readonly prompt: string; readonly value: string }> {
  const rows: Array<{ key: string; prompt: string; value: string }> = [];
  for (const q of consultIntakeQuestions(questions)) {
    const a = priorAnswers.find((x) => x.questionKey === q.key);
    if (a === undefined) continue;
    const codes = a.code !== null ? [a.code] : (a.codes ?? []);
    const labels = codes.map(
      (c) => q.options.find((o) => o.code === c)?.label ?? 'Saved choice (label unavailable)',
    );
    const detail = a.details ? clinicalDetailSummary(a.details,false) : a.text !== null && a.text.trim() !== '' ? a.text.trim() : null;
    const value =
      labels.length > 0
        ? detail !== null
          ? `${labels.join(', ')} - ${detail}`
          : labels.join(', ')
        : (detail ?? '');
    if (value === '') continue;
    rows.push({ key: q.key, prompt: consultPromptFor(q), value });
  }
  return rows;
}

/** Injectable save door default -- MODULE-LEVEL so its identity is stable
 *  across renders (the W-32 lesson). */
const DEFAULT_SAVE_API = { save: saveIntakeQuestionnaire };

/** Readiness is stricter than payload presence: conditional details are
 * required by the existing server validator, even on a partial draft. */
export function consultAnswerReady(
  question: PreconsultQuestionSpec,
  answer: QuestionnaireAnswerState[string] | undefined,
): boolean {
  return isAnswered(question, answer) &&
    (!questionNeedsDetail(question, answer) || (answer?.text ?? '').trim() !== '' || (answer?.details != null && clinicalDetailsSatisfyRequiredDetail(answer.details)));
}

const INTAKE_SUMMARY_LABELS: Readonly<Record<string, string>> = {
  'What brings the patient in today?': 'Chief complaint',
  'What is the main reason for your visit today?': 'Chief complaint',
  'When did it start?': 'Started',
  'Any pain or discomfort right now?': 'Pain or discomfort',
  'Do you have any allergies?': 'Patient-reported allergies',
  'Are you currently taking any medications?': 'Current medicines',
  'Do any of these apply to you?': 'Medical history',
  'Have you had previous treatment for this problem?': 'Previous treatment',
  'Anything else the doctor should know?': 'Additional notes',
};

/** In-memory coordination only; no second draft store or persistence policy.
 * The existing autosave still owns timers and writes. This boundary prevents
 * a lost response, a pending request or a stale retry being called unsaved. */
export interface VerifiedRestore {
  readonly valid: () => boolean;
  readonly apply: () => void;
  readonly release: () => void;
}
export interface ConsultWriterBoundary {
  readonly dirty?: () => boolean;
  readonly pending: () => boolean;
  readonly uncertain: () => boolean;
  readonly message: () => string | null;
  readonly prepareRestore: () => Promise<VerifiedRestore | null>;
  readonly check: () => Promise<boolean>;
}
type RecoveryPhase = 'ready' | 'failed' | 'unknown' | 'checking' | 'read-failed' | 'conflict' | 'typing-changed';
/** A local prerequisite failed before the existing write API was called. */
export class ConsultPreWriteError extends Error {}
export function definitiveConsultRejection(error: unknown): boolean {
  if (error instanceof ConsultPreWriteError) return true;
  // Only explicit request/domain rejections are known not to have committed.
  // A 5xx, timeout or lost transport response may follow a partial commit.
  if (!(error instanceof PortalApiError)) return false;
  if (error.status === 401 || error.status === 403) return true;
  if (error.status === 409 && ['case_sheet_finalized', 'tooth_refs_unavailable', 'prescription_changed', 'prescription_unavailable'].includes(error.code ?? '')) return true;
  // Questionnaire 409 can follow partial answer upserts. Case-sheet 400
  // 'not found' can follow tooth replacement. Neither is proof of no write.
  return error.status === 400 && error.code === 'VALIDATION_FAILED' &&
    error.message !== 'case sheet not found';
}
/** Join the intake owner registered NOW. A same-visit remount (finalized additions arriving
 *  just after Finish) registers a replacement owner while the first is still settling; the
 *  replacement is joined too, so the first owner's result never certifies the replacement's newer,
 *  unconfirmed work. A refusal is final and is never retried into a confirmation. */
export async function joinCurrentIntakeOwner(current: () => (() => Promise<boolean>) | null): Promise<boolean> {
  let owner = current();
  let saved = await (owner?.() ?? Promise.resolve(true)).catch(() => false);
  for (let next = current(); saved && next !== null && next !== owner; next = current()) {
    owner = next;
    saved = await next().catch(() => false);
  }
  return saved;
}

/** A locked historical questionnaire has no editable validation obligation.
 * Its existing write boundary must still confirm any uncertain saved result. */
export async function settleLockedIntakeBoundary(boundary:Pick<ConsultWriterBoundary,'pending'|'uncertain'|'message'|'check'>):Promise<boolean> {
  if(boundary.pending())return false;
  if((boundary.uncertain()||boundary.message()!==null)&&!await boundary.check())return false;
  return !boundary.pending()&&!boundary.uncertain()&&boundary.message()===null;
}

export function useVerifiedConsultAutosave<T>(params: Parameters<typeof useConsultAutosave>[0] & {
  readonly scope: string;
  readonly capture: () => T;
  readonly baseline: () => T;
  readonly same: (left: T, right: T) => boolean;
  readonly readVerified: () => Promise<{ value: T; restore: () => void }>;
  readonly acknowledge: (value: T) => void;
}) {
  const [, redraw] = useState(0);
  const latest = useRef(params);
  latest.current = params;
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const makeState = () => ({
    scope: params.scope, revision: 0, pending: false, paused: false,
    uncertain: false, phase: 'ready' as RecoveryPhase, attempt: null as T | null, settledFailure: false, verifiedAt: null as string | null,
  });
  const state = useRef(makeState());
  if (state.current.scope !== params.scope) state.current = makeState();
  const notify = () => { if (live.current) redraw(v => v + 1); };
  const current = (s: typeof state.current) => live.current && state.current === s;
  const message = (): string | null => {
    const s = state.current;
    if (s.pending) return 'Saving…';
    if (s.phase === 'checking') return 'Checking saved values…';
    if (s.phase === 'read-failed') return s.uncertain
      ? 'Save outcome unknown. Could not check saved values. Your typing is still here.'
      : 'Could not check saved values. Your typing is still here.';
    if (s.uncertain) return 'Save outcome unknown. Check saved values before continuing.';
    if (s.phase === 'typing-changed') return 'Your typing changed while saved values were checked. Review it before discarding.';
    if (s.phase === 'conflict') return 'Saved values changed. Your typing is still here. Discard unsaved changes to use the current record.';
    return null;
  };
  const autosave = useConsultAutosave({
    ...params,
    // A save cycle can settle after unmount. Its underlying while-loop must
    // then stop; an obsolete wrapper cannot persist or acknowledge that form.
    isDirty: () => live.current && !state.current.paused && latest.current.isDirty(),
    save: async () => {
      const s = state.current;
      if (s.paused || s.uncertain || s.phase === 'conflict' || s.phase === 'read-failed' || s.phase === 'typing-changed') {
        throw new Error(message() ?? 'Check saved values before retrying.');
      }
      const p = latest.current;
      // After a definitive rejection, inspect the current baseline before
      // retrying. Never replay the old whole form over a changed server row.
      if (s.phase === 'failed') {
        s.paused = true; s.phase = 'checking'; notify();
        let checked;
        try { checked = await p.readVerified(); }
        catch {
          if (current(s)) { s.paused = false; s.phase = 'read-failed'; notify(); }
          throw new Error('Could not check saved values. Your typing is still here.');
        }
        if (!current(s)) return;
        s.paused = false;
        if (!p.same(checked.value, p.baseline())) {
          s.phase = 'conflict'; notify();
          throw new Error(message()!);
        }
      }
      if (!current(s)) return;
      s.attempt = p.capture(); s.settledFailure = false;
      s.pending = true; s.phase = 'ready'; notify();
      try {
        await p.save();
        if (current(s)) { s.attempt = null; s.uncertain = false; s.phase = 'ready'; s.verifiedAt = p.formatNow(); }
      } catch (error) {
        if (current(s)) {
          s.uncertain = !definitiveConsultRejection(error);
          // These owned endpoint errors occur only after awaited writes have
          // ended. They may be partial, so a fresh read is still mandatory.
          // Generic database/proxy/transport errors provide no such proof.
          s.settledFailure = error instanceof PortalApiError &&
            ((error.status === 409 && error.code === 'questionnaire_persistence_unavailable') ||
             (error.status === 400 && error.code === 'VALIDATION_FAILED'));
          s.phase = s.uncertain ? 'unknown' : 'failed';
        }
        throw error;
      } finally {
        if (current(s)) { s.pending = false; notify(); }
      }
    },
  });
  const base = useRef(autosave); base.current = autosave;
  const prepareRestore = async (): Promise<VerifiedRestore | null> => {
    const s = state.current;
    if (s.pending || s.paused) return null;
    // Pause synchronously before the read, including a debounce that is about
    // to run. Restoring verified values must never invoke either write door.
    s.paused = true;
    s.phase = 'checking';
    const revision = s.revision;
    base.current.reset();
    notify();
    let checked;
    try { checked = await latest.current.readVerified(); }
    catch {
      if (current(s)) { s.paused = false; s.phase = 'read-failed'; notify(); }
      return null;
    }
    const valid = () => current(s) && s.paused && !s.pending && s.revision === revision;
    if (!valid()) {
      if (current(s)) { s.paused = false; s.phase = s.uncertain ? 'unknown' : 'typing-changed'; notify(); }
      return null;
    }
    // Seeing the prior baseline is NOT proof that an uncertain request cannot
    // still commit. Only a matching attempted value resolves this client-side.
    if (s.uncertain && !s.settledFailure && (s.attempt === null || !latest.current.same(checked.value, s.attempt))) {
      s.paused = false; s.phase = 'unknown'; notify(); return null;
    }
    const serverChanged = !latest.current.same(checked.value, latest.current.baseline());
    if (s.uncertain) {
      latest.current.acknowledge(checked.value);
      s.attempt = null;
    }
    s.uncertain = false;
    return {
      valid,
      apply: () => {
        if (!valid()) throw new Error('Visit or typing changed. Check saved values again.');
        checked.restore();
        latest.current.acknowledge(checked.value);
        s.attempt = null; s.phase = 'ready'; s.verifiedAt = latest.current.formatNow();
        base.current.reset();
        notify();
      },
      release: () => {
        if (current(s)) { s.paused = false; if (s.phase === 'checking') s.phase = serverChanged ? 'conflict' : 'ready'; notify(); }
      },
    };
  };
  const check = async (): Promise<boolean> => {
    const s = state.current;
    if (s.pending || s.paused) return false;
    const wasUncertain = s.uncertain;
    const wasConflicted = s.phase === 'conflict';
    const oldBaseline = latest.current.baseline();
    s.paused = true; s.phase = 'checking';
    base.current.reset(); notify();
    let checked;
    try { checked = await latest.current.readVerified(); }
    catch {
      if (current(s)) { s.paused = false; s.phase = 'read-failed'; notify(); }
      return false;
    }
    if (!current(s)) return false;
    s.paused = false;
    if (wasUncertain) {
      const matchedAttempt = s.attempt !== null && latest.current.same(checked.value, s.attempt);
      if (!s.settledFailure && !matchedAttempt) {
        s.phase = 'unknown'; notify(); return false;
      }
      latest.current.acknowledge(checked.value);
      s.uncertain = false; s.attempt = null; s.phase = matchedAttempt ? 'ready' : 'conflict';
      s.verifiedAt = latest.current.formatNow(); notify(); return matchedAttempt;
    }
    if (wasConflicted || !latest.current.same(checked.value, oldBaseline)) {
      s.phase = 'conflict'; notify(); return false;
    }
    s.phase = 'ready'; notify(); return true;
  };
  const boundary: ConsultWriterBoundary = {
    pending: () => state.current.pending || state.current.paused,
    uncertain: () => state.current.uncertain,
    message, prepareRestore, check,
  };
  return {
    ...autosave,
    saveState: (state.current.pending || state.current.phase === 'checking'
      ? { kind: 'saving' }
      : message() !== null ? { kind: 'error', message: message()! }
      : (autosave.saveState.kind === 'idle' || autosave.saveState.kind === 'clean')
        ? params.isDirty() ? { kind: 'dirty' }
          : state.current.verifiedAt !== null ? { kind: 'clean', savedAt: state.current.verifiedAt } : autosave.saveState
        : autosave.saveState) as ConsultSaveState,
    boundary,
    // Existing indicators can render a bounded recovery message without
    // ever displaying a false Saved status after a reset/read.
    recoveryMessage: message(),
    onEdit: () => {
      const s = state.current; s.revision++;
      if (!s.paused && !s.uncertain && s.phase !== 'conflict' && s.phase !== 'read-failed' && s.phase !== 'typing-changed') base.current.onEdit();
      notify();
    },
    flush: async () => {
      const s = state.current;
      if (s.paused || (!latest.current.enabled && latest.current.isDirty())) return false;
      if (s.uncertain || s.phase === 'read-failed' || s.phase === 'conflict' || s.phase === 'typing-changed') {
        if (!await check()) return false;
      }
      return base.current.flush();
    },
    reset: () => {
      // Reset is a fresh scope/read operation, never a way to cancel a write.
      if (!state.current.pending && !state.current.uncertain) {
        state.current.phase = 'ready'; base.current.reset();
      }
    },
  };
}


export interface ConsultIntakeQuestionsBlockProps {
  /** Existing finalized-additions writer owns persistence and exact recovery. */
  readonly addition?: {
    readonly original:PanelState;readonly missingKeys:readonly string[];readonly detailOnlyKeys:readonly string[];
    readonly pending:boolean;readonly saveState:ConsultSaveState;
    readonly onChange:(answers:readonly PreconsultAnswerInput[])=>void;readonly flush:()=>Promise<boolean>;
  };
  readonly appointmentId: number;
  readonly scopeKey?: string;
  /** Frozen V14g grouping over the same configured fields and writers. */
  readonly v14gLayout?: boolean;
  readonly profileControls?: IntakeQuestionsProfileControls;
  readonly returningPlan?: { available: boolean; labels: readonly string[] };
  readonly onPresentationChange?: (value: 'questions' | 'summary') => void;
  /** The page's initial questionnaire read. Saves and explicit recovery recheck
   *  that same API; no extra mount/idle read or second persistent store. */
  readonly read: PanelState;
  /** Injectable save door (tests + SSR). */
  readonly api?: {
    save: typeof saveIntakeQuestionnaire;
    read?: typeof getIntakeQuestionnaire;
  };
  /** R3a -- the ONE-OWNER write-through: fired with the reason text on every
   *  edit of the first question so the page routes it into the case-sheet
   *  form's chiefComplaint (the sheet's own autosave persists that store).
   *  Absent (older fixtures / doctor-v2 handoff) = no write-through; the
   *  questionnaire store still saves. */
  readonly onReasonChange?: (text: string) => void;
  /** Join the existing clinical mirror writer before folding. No new store. */
  readonly onFlushRecord?: () => Promise<boolean>;
  /** Register the same guarded save boundary for Doctor-owned navigation. */
  readonly onRegisterRecovery?: (writer: ConsultWriterBoundary | null) => void;
  readonly onDiscard?: () => Promise<boolean>;
  readonly onVerifiedRead?: (value: PreconsultReadResult) => void;
  readonly recoveryBusy?: boolean;
  readonly canDiscard?: boolean;
  readonly hasUnsavedRecord?: boolean;
  readonly onCheckAll?: () => Promise<boolean>;
  readonly onRecoveryChange?: () => void;
  readonly onRegisterBeforeLeave?: (flush: (() => Promise<boolean>) | null) => void;
  /** R3a -- seed for the reason question when the questionnaire carries no
   *  reason answer but the sheet already holds a drafted chief complaint
   *  (one owner must show the one value). */
  readonly reasonSeed?: string;
  /** R3c -- fired with the JOINED answered-key list (comma-joined string --
   *  a stable primitive, so the page's setState self-dedupes) whenever the
   *  local answer state changes; the page derives the NEXT dock's
   *  first-unanswered Go target from it. */
  readonly onAnsweredKeysChange?: (joinedKeys: string) => void;
  /** DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (C3): TRUE when the visit
   *  is finalized -- the block renders the answers READ-ONLY. */
  readonly locked?: boolean;
}

export default function ConsultIntakeQuestionsBlock({
  addition,
  appointmentId,
  scopeKey,
  v14gLayout = false,
  profileControls, returningPlan, onPresentationChange,
  read,
  api = DEFAULT_SAVE_API,
  onReasonChange,
  onFlushRecord,
  onRegisterBeforeLeave,
  onRegisterRecovery,
  onDiscard,
  onVerifiedRead,
  recoveryBusy = false,
  canDiscard = true,
  hasUnsavedRecord = false,
  onCheckAll,
  onRecoveryChange,
  reasonSeed = '',
  onAnsweredKeysChange,
  locked = false,
}: ConsultIntakeQuestionsBlockProps): JSX.Element | null {
  const additionRef=useRef(addition);additionRef.current=addition;
  const [answers, setAnswers] = useState<QuestionnaireAnswerState>({});
  const [editing, setEditing] = useState(true);
  const [detailDrafts,setDetailDrafts] = useState<Record<string,ClinicalQuestionDetailDraft>>({});
  const draftsRef=useRef(detailDrafts);draftsRef.current=detailDrafts;
  const [detailQueries,setDetailQueries] = useState<Record<string,Record<string,string>>>({});
  const queriesRef=useRef(detailQueries);queriesRef.current=detailQueries;
  const [detailError,setDetailError] = useState<string|null>(null);
  const clearDetailDrafts=()=>{draftsRef.current={};queriesRef.current={};setDetailDrafts({});setDetailQueries({});setDetailError(null);};
  const [finishing, setFinishing] = useState(false);
  const finishingRef = useRef(false);
  const writerScope = scopeKey ?? String(appointmentId);
  const scopeRef = useRef(writerScope); scopeRef.current = writerScope;
  useEffect(() => { finishingRef.current = false; setFinishing(false); }, [writerScope]);
  const [validationPrompt, setValidationPrompt] = useState<string | null>(null);
  const validationRef = useRef(validationPrompt); validationRef.current = validationPrompt;
  const [recordSaveFailed, setRecordSaveFailed] = useState(false);
  // Acknowledged persistence and a failed display refresh are different facts.
  // Keep the saved answers known, with an explicit retry for the stale projection.
  const [projectionStale, setProjectionStale] = useState(false);
  useEffect(() => { setProjectionStale(false); }, [writerScope]);
  const activeRef = useRef(true);
  const visitRef = useRef(appointmentId);
  visitRef.current = appointmentId;
  const flushRecordRef = useRef(onFlushRecord);
  flushRecordRef.current = onFlushRecord;
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);
  const beforeLeaveRef = useRef<() => Promise<boolean>>(async () => true);
  useEffect(() => {
    onRegisterBeforeLeave?.(() => beforeLeaveRef.current());
    return () => onRegisterBeforeLeave?.(null);
  }, [onRegisterBeforeLeave]);
  const summaryFocusRef = useRef<HTMLButtonElement>(null);
  const focusAfterSaveRef = useRef(false);
  const focusEditorRef = useRef(false);
  const editorRef = useRef<HTMLFieldSetElement>(null);
  // R2 -- the tick beside the LAST-TAPPED question renders from the same ONE
  // save state as the head line (two renders, one owner -- the C2 doctrine).
  const [lastEditedKey, setLastEditedKey] = useState<string | null>(null);
  // CONTINUE-CARE: the doctor's one what-changed act ('no_changes' keeps the
  // question list collapsed; 'update' opens it); null = not yet acted.
  const [carryChoice, setCarryChoice] = useState<'no_changes' | 'update' | null>(
    null,
  );
  // The keys still carrying the "from <date> - tap to change" mark (a key's
  // mark drops the moment the doctor edits that answer).
  const [carriedMarkKeys, setCarriedMarkKeys] = useState<ReadonlySet<string>>(
    new Set(),
  );
  // The carried-questions <details> open state (Update opens it; the doctor
  // can toggle freely afterwards).
  const [ccOpen, setCcOpen] = useState(false);

  const state = consultIntakeStateFromPanel(read);
  const questions = state.kind === 'ready' ? state.questions : [];
  const carry = consultCarryState(read);
  const returningSummary = v14gLayout && !addition && !locked && carry.continuity?.lastVisit != null && carryChoice !== 'update';
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  const carryConfirmation = useRef<object | null>(null);
  const [carryConfirming, setCarryConfirming] = useState(false);
  useEffect(() => { onPresentationChange?.(returningSummary ? 'summary' : 'questions'); }, [returningSummary, onPresentationChange]);
  useEffect(() => { setConfirmationError(null); carryConfirmation.current=null; setCarryConfirming(false); }, [writerScope]);
  const detailsAvailable=read.kind==='ready' && read.view.audience==='clinical' && read.view.clinicalDetailsAvailable===true;
  const richFor=(q:PreconsultQuestionSpec)=>detailsAvailable && clinicalQuestionKind(q.key)!==null &&
    (q.key!=='relevant_conditions' || q.answerKind!=='multi_select' || (q.options.some(option=>option.requiresDetail) && q.options.some(option=>option.code==='none_of_these')));
  const conditionChoiceFor=(answer:QuestionnaireAnswerState[string]|undefined)=>answer?.clear ? '' : answer?.conditionChoice ?? (answer?.codes?.length ? answer.codes.every(code=>code==='none_of_these') ? 'no' : 'yes' : '');
  const detailDraftFor=(q:PreconsultQuestionSpec,answer:QuestionnaireAnswerState[string]|undefined):ClinicalQuestionDetailDraft=>{
    const existing=draftsRef.current[q.key];if(existing)return existing;
    const value=hydrateClinicalDetails(clinicalQuestionKind(q.key)!,answer?.text??'',answer?.details);
    if(value.kind==='conditions' && q.answerKind==='multi_select' && answer?.details==null)
      value.terms=(answer?.codes??[]).filter(code=>code!=='none_of_these' && !q.options.find(option=>option.code===code)?.requiresDetail).map(code=>({source:'option',code,label:q.options.find(option=>option.code===code)?.label??code}));
    return value;
  };
  const pendingDetails=(questions:readonly PreconsultQuestionSpec[]=questionsRef.current)=>questions.find(q=>{
    if(additionRef.current&&JSON.stringify(answersRef.current[q.key])===JSON.stringify(savedAnswersRef.current[q.key])&&!Object.values(queriesRef.current[q.key]??{}).some(s=>s.trim()))return false;
    if(!richFor(q) || !(q.answerKind==='multi_select' || questionNeedsDetail(q,answersRef.current[q.key])))return false;
    if(q.answerKind==='multi_select' && conditionChoiceFor(answersRef.current[q.key])!=='yes')return false;
    const draft=detailDraftFor(q,answersRef.current[q.key]);
    if(draft.kind==='conditions' && q.answerKind==='multi_select' && !draft.terms.length && !draft.legacyText.trim())return true;
    if(clinicalDetailsPending(draft,queriesRef.current[q.key]??{}))return true;
    return false;
  });
  const detailPendingMessage=(q:PreconsultQuestionSpec)=>q.answerKind==='multi_select' && !clinicalDetailsPending(detailDraftFor(q,answersRef.current[q.key]),queriesRef.current[q.key]??{}) ? 'Choose a condition or enter its name before saving Yes. Your change is kept.' : 'Choose a suggestion or “Use as entered” for the text you typed. Add its medicine / substance before saving a reaction or note. Your text is kept.';

  // Refs the autosave engine reads (livest values, stable identities).
  const answersRef = useRef<QuestionnaireAnswerState>({});
  const savedAnswersRef=useRef<QuestionnaireAnswerState>({});
  const questionsRef = useRef<readonly PreconsultQuestionSpec[]>([]);
  questionsRef.current = questions;
  // The questions of the last ready read: a reload or failed read empties `questions`, but the
  // answers typed against them remain unsaved work until the writer saves them.
  const readyQuestionsRef = useRef<readonly PreconsultQuestionSpec[]>([]);
  if (state.kind === 'ready') readyQuestionsRef.current = questions;
  /** The serialized payload last DURABLY persisted (dirty = differs). */
  const lastSavedRef = useRef<string>('[]');
  // A clinical chief-complaint seed can be displayed without a questionnaire
  // write. Recovery must compare the real questionnaire baseline, not that
  // display seed, or an unchanged server read would look like a conflict.
  const lastVerifiedRef = useRef<string>('[]');

  const serializeCurrent = (questions: readonly PreconsultQuestionSpec[] = questionsRef.current): string =>
    JSON.stringify(
      buildQuestionnairePayload(questions, answersRef.current).filter(answer=>!answer.clear),
    );

  // R2 -- THE AUTOSAVE (the chief-complaint note's exact idiom: debounce,
  // single-flight, trailing coalesce, save-on-unmount). The save posts the
  // CURRENT answered payload through the SAME questionnaire route the
  // retired button used; on success the captured payload becomes the
  // persisted baseline so the cycle's dirty check converges.
  const readSavedProjection = async (visit: number, scope: string) => {
    const result = await (api.read ?? getIntakeQuestionnaire)(visit);
    if (!activeRef.current || visitRef.current !== visit || scopeRef.current !== scope ||
        result.appointmentId !== visit || result.audience !== 'clinical' ||
        result.underIdentityReview) throw new Error('Visit changed or saved intake is unavailable.');
    const view = consultIntakeStateFromRead(result);
    if (view.kind !== 'ready') throw new Error('Saved intake is unavailable.');
    // Publish server truth to sibling displays, never to the editable answers
    // or the clinical confirmation writer. Only verified recovery restores input.
    onVerifiedRead?.(result);
    setProjectionStale(false);
    return view;
  };
  const autosave = useVerifiedConsultAutosave({
    scope: writerScope,
    capture: () => serializeCurrent(),
    baseline: () => lastVerifiedRef.current,
    same: (a, b) => a === b,
    readVerified: async () => {
      const visit = appointmentId;
      const scope = writerScope;
      const view = await readSavedProjection(visit, scope);
      const value = JSON.stringify(buildQuestionnairePayload(view.questions, view.seeded).filter(answer=>!answer.clear));
      return { value, restore: () => {
        answersRef.current = view.seeded; setAnswers(view.seeded); clearDetailDrafts();
        setCarriedMarkKeys(new Set()); setCarryChoice(null); setCcOpen(false);
        setValidationPrompt(null); setRecordSaveFailed(false); setLastEditedKey(null);
        // Deliberately no onReasonChange: each store restores its own values.
        focusAfterSaveRef.current = true; setEditing(false);
      } };
    },
    acknowledge: value => {
      lastSavedRef.current = value;
      lastVerifiedRef.current = value;
    },
    enabled: !addition && !locked && state.kind === 'ready',
    isDirty: () => !addition && (pendingDetails() !== undefined || serializeCurrent() !== lastSavedRef.current),
    hasContent: () => pendingDetails() !== undefined || buildQuestionnairePayload(questionsRef.current,answersRef.current).length>0,
    formatNow: () => formatIstTime(new Date().toISOString()) || 'just now',
    save: async () => {
      const visit = appointmentId;
      const scope = writerScope;
      const pendingQuestion=pendingDetails();if(pendingQuestion)throw new ConsultPreWriteError(detailPendingMessage(pendingQuestion));
      const captured = serializeCurrent();
      await api.save(visit, {
        answers: buildQuestionnairePayload(
          questionsRef.current,
          answersRef.current,
        ),
      });
      if (activeRef.current && visitRef.current === visit && scopeRef.current === scope) {
        lastSavedRef.current = captured; lastVerifiedRef.current = captured;
        try {
          await readSavedProjection(visit, scope);
        } catch {
          // The POST was acknowledged. A failed GET cannot turn that known
          // save into an unknown write outcome, or cause a duplicate write.
          if (activeRef.current && visitRef.current === visit && scopeRef.current === scope) setProjectionStale(true);
        }
      }
    },
  });
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  const recheckProjection = async (button: HTMLButtonElement) => {
    const visit = appointmentId;
    const scope = writerScope;
    await (onCheckAll ? onCheckAll() : autosaveRef.current.boundary.check());
    requestAnimationFrame(() => {
      if (activeRef.current && visitRef.current === visit && scopeRef.current === scope && !button.isConnected) {
        // A successful refresh removes its retry control. Return keyboard focus
        // to the existing editor/summary door, after recovery disables settle.
        if (editing) editorRef.current?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled])')?.focus();
        else summaryFocusRef.current?.focus({ preventScroll: true });
      }
    });
  };
  useEffect(() => { onRecoveryChange?.(); }, [onRecoveryChange, autosave.recoveryMessage, autosave.saveState.kind, validationPrompt]);
  useEffect(() => {
    const writer: ConsultWriterBoundary = {
      dirty:()=>additionRef.current!==undefined&&(pendingDetails()!==undefined||serializeCurrent()!==lastSavedRef.current),
      pending: () => autosaveRef.current.boundary.pending(),
      uncertain: () => autosaveRef.current.boundary.uncertain(),
      message: () => autosaveRef.current.boundary.message() ?? (validationRef.current ? 'Add details: ' + validationRef.current : null),
      prepareRestore: async () => {
        if(!additionRef.current)return autosaveRef.current.boundary.prepareRestore();
        const captured=JSON.stringify([answersRef.current,draftsRef.current,queriesRef.current]),scope=scopeRef.current;
        // The joined additions owner fresh-reads and remounts this local editor
        // on restoration. This boundary owns no second write.
        return {valid:()=>scope===scopeRef.current&&captured===JSON.stringify([answersRef.current,draftsRef.current,queriesRef.current]),apply:()=>{},release:()=>{}};
      },
      check: () => autosaveRef.current.boundary.check(),
    };
    onRegisterRecovery?.(writer);
    return () => onRegisterRecovery?.(null);
  }, [onRegisterRecovery]);

  // The questionnaire can fail after the independent clinical mirror saved.
  // Its own unsaved work still needs the existing browser leave-warning idiom.
  useEffect(() => {
    if (!addition && !shouldWarnOnNavigate(autosave.saveState)) return;
    const warn = (event: BeforeUnloadEvent): void => { if(additionRef.current && !pendingDetails() && serializeCurrent()===lastSavedRef.current)return; event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [autosave.saveState,addition]);

  // Seed the editable answers ONCE per appointment, on the read's first ready
  // state -- a later lifted-state transition can never wipe unsaved picks
  // (the W-32 lesson, unchanged). R3a: when the questionnaire carries no
  // reason answer but the sheet drafted one, the seed shows the one value.
  const seededForRef = useRef<number | null>(null);
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (seededForRef.current === appointmentId) return;
    seededForRef.current = appointmentId;
    const seeded: QuestionnaireAnswerState = { ...state.seeded };
    if (
      (seeded.reason_for_visit?.text ?? '').trim() === '' &&
      !addition && reasonSeed.trim() !== ''
    ) {
      seeded.reason_for_visit = { text: reasonSeed };
    }
    setAnswers(seeded);
    answersRef.current = seeded;
    savedAnswersRef.current=seeded;
    clearDetailDrafts();
    // Only CURRENT visit answers establish a saved summary; prior carry does not.
    setEditing(v14gLayout || buildQuestionnairePayload(state.questions, state.seeded).filter(answer=>!answer.clear).length === 0);
    // CONTINUE-CARE: a new appointment starts with the ceremony unacted --
    // the CARRY seed deliberately does NOT enter the editable state here
    // (NO SILENT WRITES: it renders and counts for nothing until the
    // doctor's explicit confirm/update act copies it in).
    setCarryChoice(null);
    setCarriedMarkKeys(new Set());
    setCcOpen(false);
    // The seeded state is the persisted baseline (a resumed, untouched form
    // is NOT dirty; a reasonSeed-only seed mirrors the already-persisted
    // sheet field, so it is not intake-dirty either).
    lastSavedRef.current = serializeCurrent();
    lastVerifiedRef.current = JSON.stringify(buildQuestionnairePayload(state.questions, state.seeded).filter(answer=>!answer.clear));
  }, [state, appointmentId, reasonSeed]);

  // R3c -- report the answered-key set upward (joined string primitive) so
  // the page's Go target tracks the LIVE answer state, not just the
  // persisted read. Same-value updates no-op upstream by construction.
  useEffect(() => {
    if (onAnsweredKeysChange === undefined) return;
    if (state.kind !== 'ready') return;
    onAnsweredKeysChange(
      questions
        .filter((q) => consultAnswerReady(q, answers[q.key]))
        .map((q) => q.key)
        .join(','),
    );
  }, [onAnsweredKeysChange, state.kind, questions, answers]);

  useEffect(() => {
    if (!editing && focusAfterSaveRef.current) {
      summaryFocusRef.current?.focus({ preventScroll: true });
      focusAfterSaveRef.current = false;
    }
    if (editing && focusEditorRef.current) {
      editorRef.current?.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled])')?.focus();
      focusEditorRef.current = false;
    }
  }, [editing]);

  // The registered leave boundary follows the CURRENT render. Loading, hidden and locked renders
  // return before the editable finishIntake below is assigned; without this they kept the earlier
  // editable callback, which flushed the record and refused when a same-visit owner remounted
  // during that flush (the post-Finish print refusal). A locked owner settles its own writer
  // (pending, failed or uncertain saves still refuse). While the read reloads or fails the writer is
  // disabled: a pending, uncertain or failed attempt (its recovery message), newer unsaved answers or
  // an unfinished detail all refuse, exactly as the writer itself would; equality with the last saved
  // payload never certifies an outstanding attempt. Only a settled, unchanged intake confirms.
  if (locked) beforeLeaveRef.current = () => settleLockedIntakeBoundary(autosaveRef.current.boundary);
  else if (state.kind === 'loading' || state.kind === 'hidden') beforeLeaveRef.current = async () => {
    const writer = autosaveRef.current.boundary;
    return !writer.pending() && !writer.uncertain() && writer.message() === null &&
      pendingDetails(readyQuestionsRef.current) === undefined &&
      serializeCurrent(readyQuestionsRef.current) === lastSavedRef.current;
  };
  if (state.kind === 'loading' || state.kind === 'hidden') return null;

  // C3 (kept): the LOCK propagates -- a finalized visit renders the recorded
  // answers as read-only rows. Nothing writable renders on a locked visit.
  if (locked) {
    return (
      <SectionShell title={CONSULT_INTAKE_PANEL_TITLE} testId="consult-intake-block">
        {state.kind === 'under_review' ? (
          <p className="pim-hint" data-testid="consult-intake-under-review">
            {CONSULT_INTAKE_UNDER_REVIEW_COPY}
          </p>
        ) : (
          <div data-testid="consult-intake-locked">
            <p className="pim-hint">
              Visit finalized - intake answers are read-only.
            </p>
            {consultIntakeLockedRows(state, read).map((row) => (
              <div className="consult-frow" key={row.key}>
                <span className="consult-frow__l">{row.prompt}</span>
                <span
                  className={
                    row.value !== null
                      ? 'consult-frow__v'
                      : 'consult-frow__v consult-frow__v--empty'
                  }
                >
                  {row.value ?? '\u2014'}
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionShell>
    );
  }

  /** One edit: state + ref + the R3a write-through + the debounced save. */
  const editAnswer = (
    key: string,
    next: (prev: QuestionnaireAnswerState) => QuestionnaireAnswerState,
  ): void => {
    if(addition?.pending)return;
    const updated = next(answersRef.current);
    if(addition&&!addition.missingKeys.includes(key))return;
    if(addition?.detailOnlyKeys.includes(key)){
      const original=answersRef.current[key];
      updated[key]={...updated[key],code:original?.code,codes:original?.codes,clear:undefined};
    }
    answersRef.current = updated;
    setAnswers(updated);
    setLastEditedKey(key);
    setValidationPrompt(null);
    setRecordSaveFailed(false);
    // An edited answer is the doctor's own -- its carry mark drops.
    setCarriedMarkKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next2 = new Set(prev);
      next2.delete(key);
      return next2;
    });
    if(addition)addition.onChange(buildQuestionnairePayload(questionsRef.current,updated));
    else autosaveRef.current.onEdit();
  };

  // CONTINUE-CARE: the ceremony is OFFERED while a carry exists, the doctor
  // has not acted, and the CURRENT visit holds no non-reason answers of its
  // own (a resumed visit renders its own state -- proven behavior).
  const carryOffered =
    !addition &&
    state.kind === 'ready' &&
    carryChoice === null &&
    consultCarryOffered(carry, questions, state.seeded);

  /** The ONE act: copy the carried answers into the editable state (never
   *  overriding a current answer), mark provenance, and fire the SAME
   *  debounced autosave POST as any tap. 'update' additionally opens the
   *  question list for edits. */
  const applyCarry = (mode: 'no_changes' | 'update'): void => {
    setCarryChoice(mode);
    setCcOpen(mode === 'update');
    // V14g confirms health history. Today's complaint, timing and pain are
    // visit-specific, so they never inherit the previous problem's answers.
    const keys = carry.carriedKeys.filter(key => !v14gLayout || !['onset', 'pain_level'].includes(key));
    setCarriedMarkKeys(new Set(keys));
    const merged: QuestionnaireAnswerState = { ...answersRef.current };
    for (const key of keys) {
      if (merged[key] === undefined && carry.seed[key] !== undefined) merged[key] = carry.seed[key];
    }
    answersRef.current = merged;
    setAnswers(merged);
    if (mode === 'update') { focusEditorRef.current = true; setEditing(true); }
    setLastEditedKey(null);
    autosaveRef.current.onEdit();
  };

  // Closing retains partial answers through the existing autosave; it is not
  // clinical completion. Invalid conditional answers still need their details.
  const finishIntake = async (closeEditor = !v14gLayout): Promise<boolean> => {
    if(locked)return settleLockedIntakeBoundary(autosaveRef.current.boundary);
    if (state.kind !== 'ready') return true;
    if (closeEditor && profileControls && !await profileControls.beforeLeave()) return false;
    const pendingQuestion=pendingDetails();
    if(pendingQuestion){
      setDetailError(detailPendingMessage(pendingQuestion));
      document.getElementById(`consult-intake-q-${pendingQuestion.key}`)?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
      return false;
    }
    setDetailError(null);
    const missingDetail = state.questions.find(q =>
      (!addition||JSON.stringify(answersRef.current[q.key])!==JSON.stringify(savedAnswersRef.current[q.key]))&&
      questionNeedsDetail(q, answersRef.current[q.key]) &&
      !consultAnswerReady(q,answersRef.current[q.key]));
    if (missingDetail) {
      setValidationPrompt(consultPromptFor(missingDetail));
      (document.getElementById(`consult-intake-detail-${missingDetail.key}`) ?? document.getElementById(`consult-intake-q-${missingDetail.key}`)?.querySelector<HTMLElement>('[role="combobox"]'))?.focus();
      return false;
    }
    setValidationPrompt(null);
    if (finishingRef.current) return false;
    finishingRef.current = true;
    setRecordSaveFailed(false);
    setFinishing(true);
    const visit = appointmentId;
    const scope = writerScope;
    if(addition){
      addition.onChange(buildQuestionnairePayload(questionsRef.current,answersRef.current));
      const captured=serializeCurrent();
      let saved=false;try{saved=await addition.flush();}finally{if(activeRef.current&&scopeRef.current===scope){finishingRef.current=false;setFinishing(false);}}
      if(saved&&activeRef.current&&scopeRef.current===scope&&visitRef.current===visit){lastSavedRef.current=captured;lastVerifiedRef.current=captured;savedAnswersRef.current=answersRef.current;if(closeEditor){focusAfterSaveRef.current=true;setEditing(false);}}
      return saved&&scopeRef.current===scope&&visitRef.current===visit;
    }
    const results = await Promise.allSettled([
      autosaveRef.current.flush(),
      flushRecordRef.current ? flushRecordRef.current() : Promise.resolve(true),
    ]);
    const saved = results[0].status === 'fulfilled' && results[0].value;
    const recordSaved = results[1].status === 'fulfilled' && results[1].value;
    if (!activeRef.current || visitRef.current !== visit || scopeRef.current !== scope) return false;
    // Both existing writers settle independently; one failure cannot hide a pending second writer.
    if (!activeRef.current || visitRef.current !== visit) return false;
    finishingRef.current = false; setFinishing(false);
    setRecordSaveFailed(saved && !recordSaved);
    if (saved && recordSaved && closeEditor) { focusAfterSaveRef.current = true; setEditing(false); }
    return saved && recordSaved;
  };

  beforeLeaveRef.current = finishIntake;

  const shownSaveState=addition?.saveState??autosave.saveState;
  const saveLine = addition?consultIntakeSaveLine(shownSaveState):autosave.recoveryMessage ?? consultIntakeSaveLine(autosave.saveState);
  const saveTone = consultSaveStateTone(shownSaveState);
  const hasUnsavedChanges = hasUnsavedRecord || pendingDetails() !== undefined || serializeCurrent() !== lastSavedRef.current;

  const draftControls = <>
{v14gLayout && saveLine !== null && autosave.saveState.kind !== 'error' ? <span className={`consult-intake__savestate consult-intake__savestate--${saveTone}`} data-testid="consult-intake-save-state" role="status">{shownSaveState.kind === 'clean' ? `✓ ${saveLine}` : saveLine}</span> : null}
<p className="pim-hint" data-testid="consult-intake-draft-explanation">{addition?'Missing answers are saved as a dated addition. Original answers remain unchanged.':'Drafts save automatically. Save intake also closes this editor. Discard keeps saved drafts.'}</p>
        <div className="consult-intake-option-a__actions">
          {v14gLayout ? <AcButton variant="ghost" disabled={finishing || recoveryBusy} onClick={()=>void finishIntake(true)} data-testid="consult-intake-close">Close</AcButton> : null}
          <AcButton variant="primary" title={CONSULT_INTAKE_INTRO} disabled={finishing || recoveryBusy || carryOffered} onClick={() => void finishIntake(true)} data-testid="consult-intake-save">{finishing ? 'Saving…' : 'Save intake'}</AcButton>
          <AcButton variant="secondary" disabled={finishing || recoveryBusy} onClick={() => void finishIntake(true)} data-testid="consult-intake-keep-close">Keep draft &amp; close</AcButton>
          {onDiscard ? <AcButton variant="ghost" disabled={!hasUnsavedChanges || !canDiscard || finishing || recoveryBusy || autosave.boundary.pending() || autosave.boundary.uncertain()} onClick={() => void onDiscard()} data-testid="consult-intake-discard">Discard unsaved changes</AcButton> : null}
          {autosave.recoveryMessage && !projectionStale && !autosave.boundary.pending() ? <AcButton variant="secondary" disabled={recoveryBusy} onClick={() => void (onCheckAll ? onCheckAll() : autosave.boundary.check())} data-testid="consult-intake-check-saved">Check saved values</AcButton> : null}
        </div>
        {!hasUnsavedChanges && canDiscard && !autosave.boundary.pending() && !autosave.boundary.uncertain() ? <p className="pim-hint" data-testid="consult-intake-no-unsaved">No unsaved changes to discard.</p> : null}
  </>;

  /** The Continue-Care head: last-visit line, safety facts (or the labeled
   *  absence), the 1-click previous-intake door, and -- while unacted --
   *  the ONE what-changed question. Renders ONLY for a returning patient
   *  (continuity/carry served); a new patient's block is byte-identical to
   *  the one-flow render. */
  const continueCareHead = (): JSX.Element | null => {
    if(addition)return null;
    if (carry.continuity === null && carry.kind !== 'carry') return null;
    const cc = carry.continuity;
    const fact = (label: string, values: readonly string[]): JSX.Element | null =>
      values.length === 0 ? null : (
        <p
          className="consult-cc__fact"
          data-testid={`consult-cc-fact-${label.toLowerCase()}`}
        >
          <strong>{label}:</strong> {values.join(', ')}
        </p>
      );
    const priorRows =
      carry.kind === 'carry' ? consultCarryRows(questions, carry.priorAnswers) : [];
    return (
      <div className="consult-cc" data-testid="consult-continue-care">
        {cc !== null && cc.lastVisit !== null ? (
          <p className="consult-cc__lastvisit" data-testid="consult-cc-lastvisit">
            Last visit {formatIstShortDate(cc.lastVisit.visitDateIso)} -{' '}
            {cc.lastVisit.summaryLine}
          </p>
        ) : null}
        {cc !== null && cc.intakeFacts === null ? (
          <p className="pim-hint" data-testid="consult-cc-intake-absent">
            {historyIntakeAbsentCopy(cc.intakeAvailability)}
          </p>
        ) : null}
        {cc !== null && cc.intakeFacts !== null ? (
          <div className="consult-cc__safety" data-testid="consult-cc-safety">
            {fact('Allergies', cc.intakeFacts.allergies)}
            {fact('Medications', cc.intakeFacts.medications)}
            {fact('Conditions', cc.intakeFacts.conditions)}
            <p className="clinic-workbench__muted" style={{ margin: 0 }} data-testid="consult-cc-provenance">
              {cc.intakeFacts.provenanceLabel}
              {carry.kind === 'carry'
                ? ` - ${consultCarryConfirmLine(carry.lastConfirmedLabel)}`
                : ''}
            </p>
          </div>
        ) : null}
        {priorRows.length > 0 ? (
          <details
            className="consult-cc__prior"
            data-testid="consult-cc-previous-intake"
          >
            <summary>
              Previous intake ({priorRows.length} answers, {carry.fromDateLabel})
            </summary>
            {priorRows.map((row) => (
              <div className="consult-frow" key={row.key}>
                <span className="consult-frow__l">{row.prompt}</span>
                <span className="consult-frow__v">{row.key==='onset' && cc?.intakeFacts?.onsetLabel ? cc.intakeFacts.onsetLabel : row.key==='pain_level' && cc?.intakeFacts?.severityLabel ? cc.intakeFacts.severityLabel : row.value}</span>
              </div>
            ))}
          </details>
        ) : null}
        {carryOffered && !v14gLayout ? (
          <IntakeQuestionSection
            id="consult-carry-question"
            title={CONSULT_CARRY_QUESTION}
            testId="consult-carry-question"
          >
            <IntakeChoiceChips
              ariaLabel={CONSULT_CARRY_QUESTION}
              options={[
                { code: 'no_changes', label: CONSULT_CARRY_NO_CHANGES_LABEL },
                { code: 'update', label: CONSULT_CARRY_UPDATE_LABEL },
              ]}
              isSelected={() => false}
              onPick={(code) =>
                applyCarry(code === 'update' ? 'update' : 'no_changes')
              }
              testId="consult-carry"
            />
          </IntakeQuestionSection>
        ) : null}
      </div>
    );
  };

  const body = (): JSX.Element => {
    if (state.kind === 'under_review') {
      return (
        <p className="pim-hint" data-testid="consult-intake-under-review">
          {CONSULT_INTAKE_UNDER_REVIEW_COPY}
        </p>
      );
    }
    if (returningSummary) {
      const cc = carry.continuity!;
      const currentHistory = read.kind === 'ready' && read.view.audience === 'clinical' ? read.view.resumeAnswers ?? [] : [];
      const hasCurrentHistory = profileControls?.confirmedThisVisit || currentHistory.some(a=>!['reason_for_visit','onset','pain_level'].includes(a.questionKey));
      const summaryAnswers = hasCurrentHistory ? currentHistory : carry.priorAnswers;
      const prior = consultCarryRows(questions, summaryAnswers);
      const historyValue = (key:string) => {
        const answer=summaryAnswers.find(a=>a.questionKey===key);
        if(key==='relevant_conditions' && answer?.codes?.length===1 && answer.codes[0]==='none_of_these')return 'No medical conditions';
        if(key==='medications' && answer?.code==='none')return 'No regular medicines';
        if(key==='previous_treatment' && answer?.code==='no')return 'No earlier dental treatment';
        return prior.find(row=>row.key===key)?.value ?? 'Not asked';
      };
      const reportedHistory = ['relevant_conditions','medications','previous_treatment'].map(historyValue);
      const reportedMedicines = prior.find(row=>row.key==='allergies')?.value ?? null;
      const profile = profileControls?.profile;
      // The doctor-confirmed record (the masthead's source) is shown with its
      // source beside the patient's own answers; a missing answer stays Not asked.
      const confirmedMedicines = profileControls?.available ? profile?.medicationAllergies?.trim() || null : null;
      const confirmedHistory = profileControls?.available ? profile?.medicalAlerts?.trim() || null : null;
      const reportedSource = hasCurrentHistory ? 'Patient-reported today' : 'Patient-reported';
      const source = (label:string) => <span className="consult-returning-source"> · {label}</span>;
      return <div data-testid="consult-returning-summary">
        <div className="consult-returning-today">
          <div className="consult-returning-small">Today’s visit</div>
          <div className="consult-returning-complaint">Today: {answers.reason_for_visit?.text?.trim() || reasonSeed || 'Reason not recorded'}</div>
          <dl><dt>Treatment plan</dt><dd>{returningPlan?.available ? returningPlan.labels.length ? returningPlan.labels.map((label,index)=><div key={index}>{label}</div>) : 'No saved treatment plan' : 'Treatment plan could not be checked'}</dd>
            <dt>Last visit</dt><dd>{cc.lastVisit ? `${formatIstShortDate(cc.lastVisit.visitDateIso)} · ${cc.lastVisit.summaryLine}` : 'Not recorded'}</dd>
            <dt>Allergies</dt><dd data-testid="consult-returning-allergies">Anaesthesia: {profileControls?.available ? profile?.anaesthesiaAllergy === 'yes' ? 'Yes' : profile?.anaesthesiaAllergy === 'no' ? 'No' : 'Not asked' : 'Unavailable'}{profileControls?.available && profile?.anaesthesiaAllergy ? source('Doctor record') : null}
              {' · '}Medicines: {confirmedMedicines ? <><b className="consult-returning-alert">{confirmedMedicines}</b>{source('Doctor record')}</> : <>{reportedMedicines ?? 'Not asked'}{reportedMedicines ? source(reportedSource) : null}</>}
              {confirmedMedicines && reportedMedicines && !reportedMedicines.toLocaleLowerCase('en').includes(confirmedMedicines.toLocaleLowerCase('en')) ? <div>Patient said: {reportedMedicines}{source(reportedSource)}</div> : null}</dd>
            <dt>History</dt><dd data-testid="consult-returning-history">{confirmedHistory ? <div>{confirmedHistory}{source('Doctor record')}</div> : null}
              {!confirmedHistory || reportedHistory.some(value => value !== 'Not asked') ? <div>{reportedHistory.join(' · ')}{reportedHistory.some(value => value !== 'Not asked') ? source(reportedSource) : null}</div> : null}</dd></dl>
          <div className="consult-returning-small">{profile?.lastConfirmedAt ? `Last confirmed ${formatIstShortDate(profile.lastConfirmedAt)}` : 'Confirmation not recorded'} · {hasCurrentHistory ? 'Today’s answers are patient-reported' : 'Prior answers are patient-reported'}</div>
        </div>
        <div className="consult-v14g__card"><div className="consult-returning-actions"><h3>Any changes in health or medicines{cc.lastVisit ? ` since ${formatIstShortDate(cc.lastVisit.visitDateIso)}` : ''}?</h3>
          <AcButton variant="secondary" disabled={finishing || recoveryBusy || carryConfirming || profileControls?.busy} onClick={()=>applyCarry('update')} data-testid="consult-carry-update">Update</AcButton>
          <AcButton variant="primary" disabled={finishing || recoveryBusy || carryConfirming || carry.kind!=='carry' || !profileControls?.canConfirm} onClick={()=>{
            if(carryConfirmation.current)return;
            const scope=writerScope,attempt={};carryConfirmation.current=attempt;setCarryConfirming(true);setConfirmationError(null);applyCarry('no_changes');
            void finishIntake().then(async saved=>{
              if(!saved || !activeRef.current || scopeRef.current!==scope)return false;
              return profileControls?.confirm() ?? false;
            }).then(confirmed=>{if(activeRef.current && scopeRef.current===scope && !confirmed)setConfirmationError('Confirmation did not finish. Your answers are kept. Check the saved record and try again.');}).finally(()=>{
              if(activeRef.current && scopeRef.current===scope && carryConfirmation.current===attempt){carryConfirmation.current=null;setCarryConfirming(false);}
            });
          }} data-testid="consult-carry-no_changes">{finishing || carryConfirming || profileControls?.busy ? 'Saving…' : profileControls?.confirmedThisVisit ? 'Confirmed for today' : 'No changes'}</AcButton>
          </div><p className="consult-returning-small">“No changes” confirms the history above for today. It does not copy the old complaint, and anything never asked stays “Not asked”.</p>
          {carry.kind!=='carry' ? <p role="status">Previous intake answers are unavailable. Use Update to record today’s answers.</p> : null}
          {confirmationError ? <p role="alert">{confirmationError}</p> : null}
        </div>
        <details className="consult-v14g__card"><summary>Previous answers and confirmed record</summary>{continueCareHead()}{profileControls?.details}</details>
      </div>;
    }
    if (!editing) {
      return <div className="consult-intake-option-a__summary" data-testid="consult-intake-summary">
        {continueCareHead()}
        {state.questions.map(q => {
          const answer = answers[q.key];
          const selected = q.options.filter(o => isOptionSelected(answer, q.answerKind, o.code));
          const detail = answer?.clear ? '' : q.answerKind === 'short_text' || questionNeedsDetail(q, answer)
            ? (answer?.details ? clinicalDetailSummary(answer.details,false) : (answer?.text ?? '').trim()) : answer?.details ? clinicalDetailSummary(answer.details,false) : '';
          const value = [...selected.filter(o => !(answer?.details?.kind === 'conditions' && o.requiresDetail)).map(o => o.label), detail].filter(Boolean).join(' — ');
          return <div className="consult-frow" key={q.key} id={`consult-intake-q-${q.key}`} tabIndex={-1}>
            <span className="consult-frow__l" title={consultPromptFor(q)}>{INTAKE_SUMMARY_LABELS[consultPromptFor(q)] ?? consultPromptFor(q)}</span>
            <span className={value ? 'consult-frow__v' : 'consult-frow__v consult-frow__v--empty'}>{value || '\u2014'}</span>
          </div>;
        })}
      </div>;
    }
    const renderQuestion = (q: PreconsultQuestionSpec): JSX.Element => {
          const prompts: Record<string, string> = {reason_for_visit:'Why the patient came',onset:'Since when',pain_level:'Pain',allergies:'Allergic to any medicines?',relevant_conditions:'Any medical condition?',medications:'Taking any medicines regularly?',previous_treatment:'Any earlier dental treatment?'};
          const prompt = v14gLayout ? (prompts[q.key] ?? consultPromptFor(q)) : consultPromptFor(q);
          const answer = answers[q.key];
          const recorded=addition&&!addition.missingKeys.includes(q.key);
          const choiceRecorded=addition?.detailOnlyKeys.includes(q.key)===true;
          if(recorded){const codes=answer?.code?[answer.code]:answer?.codes??[];return <div className="consult-frow" key={q.key}><span className="consult-frow__l">{prompt}</span><span className="consult-frow__v">{[...codes.map(code=>q.options.find(o=>o.code===code)?.label??code),answer?.details?clinicalDetailSummary(answer.details,false):answer?.text].filter(Boolean).join(' — ')||'Not recorded'}</span></div>;}
          const needsDetail = questionNeedsDetail(q, answer);
          const isReason = q.key === 'reason_for_visit';
          const rich=richFor(q);
          const richInput=rich ? <ClinicalQuestionDetailsInput draft={detailDraftFor(q,answer)} queries={detailQueries[q.key]??{}}
            conditionOptions={q.answerKind==='multi_select'?q.options.filter(option=>!option.requiresDetail && option.code!=='none_of_these').map(option=>({source:'option',code:option.code,label:option.label} as ClinicalQuestionTerm)):[]}
            disabled={locked || finishing || recoveryBusy || addition?.pending===true}
            onQueriesChange={value=>{const next={...queriesRef.current,[q.key]:value};queriesRef.current=next;setDetailQueries(next);setDetailError(null);setLastEditedKey(q.key);if(addition)onRecoveryChange?.();else autosaveRef.current.onEdit();}}
            onChange={draft=>{
              const next={...draftsRef.current,[q.key]:draft};draftsRef.current=next;setDetailDrafts(next);setDetailError(null);
              const stored=serializeClinicalDetails(draft);
              editAnswer(q.key,prev=>{
                const old=prev[q.key]??{};
                const codes=draft.kind==='conditions' && q.answerKind==='multi_select' ? draft.terms.flatMap(term=>term.source==='option'?[term.code]:[]) : old.codes;
                if(draft.kind==='conditions' && q.answerKind==='multi_select' && (draft.legacyText.trim() || draft.terms.some(term=>term.source!=='option'))){
                  const other=q.options.find(option=>option.requiresDetail)?.code;if(other && !codes!.includes(other))codes!.push(other);
                }
                return {...prev,[q.key]:{...old,clear:undefined,...(draft.kind==='conditions'?{conditionChoice:'yes' as const}:{}),...(codes===undefined?{}:{codes}),...(stored===null?{}:{text:stored.legacyText,details:stored})}};
              });
            }} /> : null;
          return (
            <IntakeQuestionSection
              key={q.key}
              id={`consult-intake-q-${q.key}`}
              title={prompt}
              required={q.required}
              answered={v14gLayout ? undefined : consultAnswerReady(q, answer)}
              hint={
                v14gLayout && ['onset','pain_level'].includes(q.key) && !q.required ? 'optional' :
                isReason
                  ? (v14gLayout ? null : consultReasonHint(read))
                  : carriedMarkKeys.has(q.key)
                    ? consultCarryMark(carry.fromDateLabel)
                    : v14gLayout && q.key === 'relevant_conditions'
                      ? 'Sugar, BP, heart, pregnancy, other'
                      : null
              }
              testId={`consult-intake-q-${q.key}`}
            >
              {q.answerKind === 'short_text' ? (
                <textarea
                  rows={isReason ? 2 : 1}
                  className="pim-input"
                  aria-label={prompt}
                  value={answer?.text ?? ''}
                  maxLength={500}
                  onChange={(e) => {
                    const text = e.target.value;
                    editAnswer(q.key, (prev) => ({
                      ...prev,
                      [q.key]: { ...(prev[q.key] ?? {}), text },
                    }));
                    // R3a ONE OWNER: the reason text IS the chief complaint --
                    // the page routes it into the sheet form (its autosave
                    // persists that store; the rail mirrors it read-only).
                    if (isReason&&!addition) onReasonChange?.(text);
                  }}
                />
              ) : (
                q.answerKind === 'multi_select' ? rich ? <>
                  <div className="consult-question-answers"><IntakeChoiceChips disabled={choiceRecorded} ariaLabel={prompt}
                    options={[{code:'yes',label:'Yes'},{code:'no',label:'No'},{code:'',label:'Not asked'}]}
                    isSelected={code=>conditionChoiceFor(answer)===code}
                    onPick={value=>editAnswer(q.key,prev=>({...prev,[q.key]:value==='no' ? {codes:['none_of_these'],details:null,conditionChoice:'no'} : value==='yes' ? {...(prev[q.key]??{}),clear:undefined,codes:(prev[q.key]?.codes??[]).filter(code=>code!=='none_of_these'),conditionChoice:'yes'} : {clear:true}}))}/></div>
                  {conditionChoiceFor(answer)==='yes' ? richInput : null}
                </> : <div className="consult-intake-checkboxes" data-testid={`consult-intake-opt-${q.key}`}>
                  {q.options.map(option => <label key={option.code}>
                    <Checkbox disabled={choiceRecorded} checked={isOptionSelected(answer, q.answerKind, option.code)}
                      onCheckedChange={() => editAnswer(q.key, prev => ({...prev, [q.key]: toggleMultiOption(prev[q.key], option.code)}))} />
                    {option.label}
                  </label>)}
                </div> : rich && q.options.length===2 && q.options.some(o=>o.code==='yes') && q.options.some(o=>o.code===(q.key==='allergies'?'none_known':'none')) ? <div className="consult-question-answers" data-allergy={q.key==='allergies'}><IntakeChoiceChips disabled={choiceRecorded} ariaLabel={prompt}
                  options={[{code:'yes',label:'Yes'},{code:q.key==='allergies'?'none_known':'none',label:'No'},{code:'',label:'Not asked'}]}
                  isSelected={code=>(answer?.code??'')===code}
                  onPick={code=>editAnswer(q.key,prev=>({...prev,[q.key]:code ? {...(prev[q.key]??{}),code,clear:undefined} : {clear:true}}))}/></div> : v14gLayout && q.options.length <= 8 ? <div className="consult-question-answers"><IntakeChoiceChips disabled={choiceRecorded} ariaLabel={prompt}
                  options={[...(q.key==='previous_treatment' && q.options.length===2 && q.options.some(o=>o.code==='yes') && q.options.some(o=>o.code==='no') ? ['yes','no'].map(code=>q.options.find(o=>o.code===code)!) : q.options).map(option=>({code:option.code,label:option.label})),...(q.key==='previous_treatment'?[{code:'',label:'Not asked'}]:[])]}
                  isSelected={code=>(answer?.code??'')===code}
                  onPick={code=>editAnswer(q.key,prev=>({...prev,[q.key]:code?{...(prev[q.key]??{}),code,clear:undefined}:{clear:true}}))}/></div> : <ChoiceSelect disabled={choiceRecorded} label={prompt} value={answer?.code ?? ''}
                  options={[{value:'',label:rich?'Not asked':'Not answered',disabled:!rich},...q.options.map(option=>({value:option.code,label:option.label}))]}
                  onChange={code => editAnswer(q.key, prev => ({...prev,[q.key]:code ? {...(prev[q.key] ?? {}),code,clear:undefined} : {clear:true}}))} />
              )}
              {needsDetail && q.answerKind!=='multi_select' && rich ? richInput : needsDetail && !rich ? (
                <input
                  className="pim-input"
                  style={{ marginTop: 'var(--ac-space-2)' }}
                  id={`consult-intake-detail-${q.key}`}
                  aria-label={`${prompt} - details`}
                  placeholder={v14gLayout && q.key==='previous_treatment' ? 'What and when?' : 'Add details'}
                  value={answer?.text ?? ''}
                  maxLength={500}
                  onChange={(e) => {
                    const text = e.target.value;
                    editAnswer(q.key, (prev) => ({
                      ...prev,
                      [q.key]: { ...(prev[q.key] ?? {}), text },
                    }));
                  }}
                />
              ) : null}
              {/* R2 -- the tapped-question tick: the SAME one save state,
                  rendered small at the point of the act (clean only). */}
              {lastEditedKey === q.key && autosave.saveState.kind === 'clean' ? (
                <p
                  className="consult-intake__qtick"
                  data-testid={`consult-intake-saved-${q.key}`}
                >
                  {'\u2713'} {CONSULT_INTAKE_SAVED_TICK}
                </p>
              ) : null}
            </IntakeQuestionSection>
          );
    };
    const head = continueCareHead();
    const reasonQ = state.questions.find((q) => q.key === 'reason_for_visit');
    const restQ = state.questions.filter((q) => q.key !== 'reason_for_visit');
    const renderGroups = (items: readonly PreconsultQuestionSpec[]): JSX.Element => {
      if (!v14gLayout) return <>{items.map(renderQuestion)}</>;
      const groups = [
        { key: 'concern', title: 'Why the patient came', keys: ['reason_for_visit', 'onset', 'pain_level'] },
        { key: 'allergies', title: 'Allergies', keys: ['allergies'] },
        { key: 'history', title: 'Medical / dental history', keys: ['relevant_conditions', 'medications', 'previous_treatment'] },
      ];
      const known = new Set(groups.flatMap(group => group.keys));
      const additional = items.filter(q => !known.has(q.key));
      return <>{groups.map(group => {
        const selected = group.keys.flatMap(key => items.filter(q => q.key === key));
        return selected.length || (group.key === 'allergies' && profileControls) ? <div className="consult-v14g__card consult-v14g__question-card" key={group.key} data-testid={`consult-question-group-${group.key}`}>
          <div className="consult-question-card-heading"><h3>{group.title}{group.key === 'concern' ? <small>Chief complaint</small> : group.key === 'allergies' ? <small>Asked at every first visit · as on the Partha case sheet</small> : null}</h3>
            {group.key === 'concern' && read.kind === 'ready' && read.view.answers.some(a=>a.questionKey==='reason_for_visit') ? <span className="consult-question-source">{read.view.audience === 'clinical' ? read.view.provenanceLabel : 'Patient-reported'}</span> : null}
          </div>
          {group.key === 'allergies' ? profileControls?.question : null}
          {selected.map(renderQuestion)}
        </div> : null;
      })}
      {additional.filter(q=>q.required).map(renderQuestion)}
      {(additional.some(q=>!q.required) || profileControls) ? <details className="consult-v14g__card consult-question-more" data-testid="consult-question-more" open={profileControls?.attention || undefined}><summary>Additional questions and confirmed record</summary>
        {additional.filter(q=>!q.required).map(renderQuestion)}{profileControls?.details}
      </details> : null}</>;

    };
    return (
      <div className="consult-intake-option-a">
        <fieldset ref={editorRef} aria-label="Intake answers" disabled={finishing || recoveryBusy || addition?.pending} className="consult-intake-option-a__fields">
        {!v14gLayout ? head : null}
        {/* The chief complaint stays fresh; carry remains an explicit act and
            never overwrites this visit's own answers. Grouping moves no writer. */}
        {v14gLayout ? renderGroups(state.questions) : carryOffered || carryChoice !== null ? <>
          {reasonQ ? renderGroups([reasonQ]) : null}
          {carryOffered ? null : <details className="consult-cc__questions" data-testid="consult-cc-questions"
            open={ccOpen} onToggle={e=>setCcOpen((e.target as HTMLDetailsElement).open)}>
            <summary data-testid="consult-cc-confirmed">
              {carryChoice === 'no_changes' ? consultCarryConfirmedLine(carry.fromDateLabel) : `Intake questions - carried answers prefilled (${carry.fromDateLabel})`}
              {autosave.saveState.kind === 'clean' ? ` ${'\u2713'} ${CONSULT_INTAKE_SAVED_TICK}` : ''}
            </summary>
            {renderGroups(restQ)}
          </details>}
        </> : renderGroups(state.questions)}
        </fieldset>
        {v14gLayout ? <details className="consult-question-save-details"><summary>Draft saving and recovery</summary>{draftControls}</details> : draftControls}
        {detailError ? <p role="alert" data-testid="consult-question-detail-error">{detailError}</p> : null}
        {validationPrompt !== null || autosave.saveState.kind === 'error' || recordSaveFailed ? (
          <p className="pim-error" role="alert" data-testid="consult-intake-error">
            {validationPrompt !== null ? `Add details: ${validationPrompt}` : recordSaveFailed ? 'Visit notes need attention before closing intake.' : (autosave.recoveryMessage ?? consultIntakeSaveLine(autosave.saveState))}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <SectionShell
      embedded={v14gLayout}
      title={CONSULT_INTAKE_PANEL_TITLE}
      actions={(!v14gLayout || !editing) && state.kind === 'ready' ? (
        <div className="consult-intake-option-a__actions">
          {saveLine !== null && autosave.saveState.kind !== 'error' ? (
            <span className={`consult-intake__savestate consult-intake__savestate--${saveTone}`} data-testid="consult-intake-save-state" role="status">
              {shownSaveState.kind === 'clean' ? `${'\u2713'} ${saveLine}` : saveLine}
            </span>
          ) : null}
          {!editing ? (
            <AcButton variant="secondary" ref={summaryFocusRef} onClick={() => { focusEditorRef.current = true; setEditing(true); }} data-testid="consult-intake-edit">Edit intake</AcButton>
          ) : (
            <AcButton variant="ghost" disabled={finishing || recoveryBusy} onClick={() => void finishIntake(true)} data-testid="consult-intake-close">Close</AcButton>
          )}
        </div>
      ) : undefined}
      testId="consult-intake-block"
    >
      {addition?<details className="consult-cc__prior"><summary>Original answers</summary>{consultIntakeLockedRows(consultIntakeStateFromPanel(addition.original),addition.original).map(row=><div className="consult-frow" key={row.key}><span className="consult-frow__l">{row.prompt}</span><span className="consult-frow__v">{row.value??'Not recorded'}</span></div>)}</details>:null}
      {body()}
      {projectionStale ? (
        <div data-testid="consult-intake-refresh-status">
          <p className="pim-error" role="status">The patient-reported safety display may be out of date. Check saved values to refresh it.</p>
          <AcButton variant="secondary" disabled={finishing || recoveryBusy || autosave.boundary.pending()} onClick={event => void recheckProjection(event.currentTarget)} data-testid="consult-intake-refresh-saved">Check saved values</AcButton>
        </div>
      ) : null}
    </SectionShell>
  );
}
